import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { CartCharges, CartItem, CartItemType, CartSectionType, CartSelectedCustomization } from '../../../../../../models/order/workflow/order-cart.model';
import { OrderFlowType, ProductType } from '../../../../../../models/order/order.model';
import { VoiceProductResponseService } from '../../order-api-response/voice-pd-response.service';
import { VoiceCartData } from '../../../../../../models/order/workflow/voice/voice-cart.model';
import { OrderCustomizationsStateService } from '../order-customization/order-customization.state.service';
import { VoicePackageSelectionStateService } from '../voice/voice-package-selection/voice-package-selection-state.service';


@Injectable({ providedIn: 'root' })
export class VoiceCartStateService {
    private voicePackageStateService = inject(VoicePackageSelectionStateService);
    private voicePdResponseService = inject(VoiceProductResponseService);
    private orderCustomizationsStateService = inject(OrderCustomizationsStateService);
    private selectedCustomizations: CartSelectedCustomization[] = [];
    private _cart = signal<VoiceCartData | null>(null);
    private orderCustomizations = this.orderCustomizationsStateService.selectedCustomizations;
    cart = this._cart.asReadonly();

    constructor() {
        effect(() => {
            const order = this.orderCustomizations();
            const pdResponse = this.voicePdResponseService.productResponse();
            this._cart.set(null);
            this.selectedCustomizations = [];
            if (pdResponse && this.voicePackageStateService.isPlanSelected()) {
                this.setupSelectedCustomizations();
                this.setupCartFromOrder();
            }
            order; // keep the effect reactive on customization changes
        });
    }

    private setupCartFromOrder() {
        const voiceCharges = this.setupVoiceCharges();
        if (voiceCharges) {
            voiceCharges.firstBillCharges = this.setupChargesByCartCategory('firstbill', CartSectionType.FirstBill, CartItemType.Fee);
            voiceCharges.todaysCharges = this.setupChargesByCartCategory('todayscharges', CartSectionType.TodaysCharges, CartItemType.Fee);
            voiceCharges.packagesCharges = this.setupChargesByCartCategory('package', CartSectionType.Packages, CartItemType.Fee);
            voiceCharges.savingAndPromotionCharges = this.setupChargesByCartCategory('savingandpromotions', CartSectionType.SavingsAndPromotions, CartItemType.Promotion);
        }

        this._cart.set(voiceCharges);
    }

    private setupSelectedCustomizations() {
        const voiceCustomizations = this.orderCustomizations().filter(c => c.type === OrderFlowType.Voice);
        if (!voiceCustomizations.length) {
            this.selectedCustomizations = [];
            return;
        }

        const selectedCustomizations: CartSelectedCustomization[] = voiceCustomizations
            .map(c => ({ Customization: c.customization, Choice: c.choice }));
        selectedCustomizations.sort((a, b) => (a.Choice?.CartSeq || 0) - (b.Choice?.CartSeq || 0));
        this.selectedCustomizations = selectedCustomizations;
    }

    private setupChargesByCartCategory(cartCategory: string, cartSectionType: CartSectionType, itemType: CartItemType): CartCharges {
        const charges: CartCharges = { totalAmount: 0, items: [] };

        for (const offer of this.selectedCustomizations) {
            const choiceCategory = offer.Choice?.CartCategory?.toLocaleLowerCase();
            const customizationCategory = offer.Customization?.CartCategory?.toLocaleLowerCase();
            if (choiceCategory !== cartCategory && customizationCategory !== cartCategory) {
                continue;
            }

            const cartItem = this.toCartItem(offer, cartSectionType, itemType);
            charges.items.push(cartItem);
            charges.totalAmount += cartItem.amount;
        }

        return charges;
    }

    private setupVoiceCharges(): VoiceCartData | undefined {
        const voiceComponent = this.voicePdResponseService.getProductComponent();
        if (!voiceComponent) {
            return undefined;
        }

        const voiceCartData: VoiceCartData = {
            name: voiceComponent?.ComponentName || '',
            price: voiceComponent?.Price || 0,
            promoPrice: voiceComponent?.PromoPrice || 0,
            monthly: {
                totalAmount: voiceComponent?.Price || 0,
                items: [],
                promotions: { totalAmount: 0, items: [] }
            }
        };

        for (const offer of this.selectedCustomizations) {
            const isMonthlyVoiceOffer = offer.Choice?.Fee?.FeeTerm === 'Monthly' &&
                (offer.Choice.CartCategory?.toLocaleLowerCase() === 'voice' ||
                    (offer.Choice.CartCategory == null && offer.Customization.CartCategory?.toLocaleLowerCase() === 'voice'));

            if (!isMonthlyVoiceOffer) {
                continue;
            }

            const cartItem = this.toCartItem(offer, CartSectionType.Monthly, CartItemType.Fee);
            if (cartItem.amount < 0) {
                cartItem.type = CartItemType.Promotion; // Negative amounts are promotions
                voiceCartData.monthly.promotions.items.push(cartItem);
                voiceCartData.monthly.promotions.totalAmount += cartItem.amount;
            } else {
                voiceCartData.monthly.items.push(cartItem);
                voiceCartData.monthly.totalAmount += cartItem.amount;
            }
        }

        voiceCartData.monthly.totalAmount += voiceCartData.monthly.promotions.totalAmount || 0;

        return voiceCartData;
    }

    private toCartItem(offer: CartSelectedCustomization, cartSectionType: CartSectionType, type: CartItemType): CartItem {
        return {
            disclosure: offer.Choice?.CartDisclosure || '',
            amount: offer.Choice?.Fee?.Fee || 0,
            name: offer.Choice?.CartName || offer.Choice?.Name || '',
            choiceCode: offer.Choice?.ChoiceCode || '',
            choice: offer.Choice,
            customization: offer.Customization,
            metadata: offer,
            type,
            cartSectionType,
            productType: ProductType.Voice
        };
    }

    isCartEmpty = computed(() => {
        return !this._cart();
    })

    getTotalTodaysCharges(): number {
        const cart = this.cart();
        const requiredTodaysCharges = cart?.todaysCharges?.items?.filter(item => item.isRequired === true || item.isRequired === null || item.isRequired === undefined);
        return requiredTodaysCharges?.reduce((total, item) => total + item.amount, 0) || 0;
    }

    getSelectedCustomizations(): CartSelectedCustomization[] {
        return this.selectedCustomizations;
    }
}
